import type React from 'react';
import {
  File,
  FileCode,
  FileCsv,
  Image,
  Link as LinkIcon,
  PaperclipHorizontal,
} from '@phosphor-icons/react';
import type { BadgeVariant } from '@chatsdet/ui';

/**
 * Shared file-type presentation for context-library documents (icon, short
 * label). Single home for the maps the library page renders, so a new file
 * type is styled in one place.
 */
export const FILE_TYPE_ICONS: Record<string, React.ElementType> = {
  pdf: File,
  image: Image,
  csv: FileCsv,
  doc: File,
  link: LinkIcon,
  paste: PaperclipHorizontal,
  'code-archive': FileCode,
  code: FileCode,
};

export const FILE_TYPE_LABELS: Record<string, string> = {
  pdf: 'PDF',
  image: 'Image',
  csv: 'CSV',
  doc: 'Doc',
  link: 'Link',
  paste: 'Paste',
  'code-archive': 'Zip',
  code: 'Code',
};

/** File types that have no downloadable file (text stored inline). */
export const NON_DOWNLOADABLE_TYPES = new Set(['paste']);

export const fileTypeIcon = (fileType: string): React.ElementType =>
  FILE_TYPE_ICONS[fileType] ?? File;

export const fileTypeLabel = (fileType: string): string =>
  FILE_TYPE_LABELS[fileType] ?? fileType;

/** Map an extraction status to a badge variant for the status chip. */
export const statusBadgeVariant = (status: string): BadgeVariant => {
  switch (status) {
    case 'ready':
      return 'status-success';
    case 'failed':
      return 'status-failed';
    case 'processing':
      return 'status-running';
    default:
      return 'status-queued';
  }
};

/** Human-readable byte size, or null when unknown. */
export const formatBytes = (bytes: number | null): string | null => {
  if (bytes === null || bytes <= 0) return null;
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
};
